import { Navigate, useLocation } from 'react-router-dom'
import { getDataFromLocalStorage } from '@/utils/localStorage'
import cookieService from '@/utils/cookies'
import type { UserRole } from '@/services/auth'
import { ROUTES } from '@/constants'
import { useSessionTimeout } from '@/hooks'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Which roles are allowed to access this route. If empty, any authenticated user allowed. */
  allowedRoles?: UserRole[]
}

/**
 * ProtectedRoute — guards routes based on localStorage token and role.
 *
 * On hard refresh, Redux resets but localStorage persists. So we check
 * localStorage directly — no Redux dependency, no loading state needed.
 *
 * Flow:
 *   - token missing → redirect to login
 *   - token exists but wrong role → redirect to correct portal
 *   - token exists + correct role → render children
 */
export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const location = useLocation()
  const isLoggedIn = !!cookieService.getValue('access_token')
  useSessionTimeout()

  // Not logged in — redirect to login
  if (!isLoggedIn) {
    return <Navigate to={ROUTES.AUTH.CLINICIAN_LOGIN} state={{ from: location }} replace />
  }

  // Check role if allowedRoles specified
  if (allowedRoles) {
    const storedRole = getDataFromLocalStorage('role')
    const role = storedRole?.replace(/^"|"$/g, '') as UserRole | undefined

    // A token with no recorded portal role is not a known session — back to login rather than
    // falling through into a portal by default.
    if (!role) {
      return <Navigate to={ROUTES.AUTH.CLINICIAN_LOGIN} state={{ from: location }} replace />
    }

    if (!allowedRoles.includes(role)) {
      // Patient trying to access clinician portal → send to patient portal
      if (role === 'patient') {
        return <Navigate to={ROUTES.PATIENT.DASHBOARD} replace />
      }
      // Clinician/admin trying to access patient portal → send to clinician dashboard
      return <Navigate to={ROUTES.APP.ROOT} replace />
    }
  }

  return <>{children}</>
}
